import { z } from "zod";
/*
 * The evaluated result of a widget (docs/specs/shared-widget-blocks.md, sections 5 and 6): what the
 * evaluator returns for one widget and one viewer, and what every renderer draws from, in the app, a
 * canvas and a public link alike. A widget is a spec; its numbers are computed by the evaluator over
 * what the viewer may read, never written by an agent.
 *
 * A result is in exactly one state, and each is drawn distinctly. The rule the states exist for: a
 * figure the viewer may not see, or that is not known, must never look like zero.
 */
export const WIDGET_RESULT_STATES = ["ok", "stale", "denied", "missing", "unconfigured", "failed"];
/** A figure as it is shown and as it is drawn: the words a reader sees, and the number a scale is drawn from. */
export const widgetAmountSchema = z.object({
    /** The figure in the words and digits a reader sees ("£48.2k", "12 days"), written by the evaluator so every renderer shows the same. */
    text: z.string().min(1).max(64),
    /** The number itself, for a bar's length or a line's height; null when there is no reading. */
    number: z.number().finite().nullable(),
    /** What it is measured in: a currency code, "percent", "days", or a plain count. */
    unit: z.string().min(1).max(32).optional(),
}).strict();
/** How a status reads at a glance. */
export const WIDGET_TONES = ["neutral", "good", "warning", "bad"];
/**
 * How a connected source stood against what its data set promises when it was read: within it, later
 * than it, its last sync failed, or not known (a data set that promises nothing, or keeps no sync time).
 * Late and failed are told apart: one is waiting for the next sync, the other needs somebody.
 */
export const WIDGET_SOURCE_FRESHNESS = ["fresh", "late", "failed", "unknown"];
/** One relation a result was computed from, and how fresh it was when read. */
export const widgetSourceSchema = z.object({
    /** The warehouse relation, or the channel whose records the warehouse served. */
    relation: z.string().min(1).max(200),
    /** What people call it. */
    label: z.string().min(1).max(200).optional(),
    /** Its version or data revision when read, which a cached result is keyed on. */
    version: z.string().min(1).max(200).optional(),
    /** Source freshness: when its data was last brought up to date, in epoch milliseconds; null when not known. */
    dataAsOf: z.number().int().nonnegative().nullable(),
    /** Whether its data is brought in from a connected system. Left out for records kept in the channel itself, which people keep up to date. */
    connected: z.boolean().optional(),
    /** How a connected source stood against its freshness promise when read. */
    freshness: z.enum(WIDGET_SOURCE_FRESHNESS).optional(),
}).strict();
/**
 * Where a result came from and when: the three times the spec keeps apart. A countdown computed
 * today may rest on a date entered months ago, and both are true.
 */
export const widgetProvenanceSchema = z.object({
    sources: z.array(widgetSourceSchema).max(50),
    /** Evaluation time: when this result was computed, in epoch milliseconds. */
    evaluatedAt: z.number().int().nonnegative(),
    /** The zone its days, weeks and months are counted in. */
    timezone: z.string().min(1).max(64),
    /** The reporting period it covers, when it covers one. */
    period: z.object({
        from: z.number().int().nonnegative(), to: z.number().int().nonnegative(), label: z.string().min(1).max(80).optional(),
    }).strict().optional(),
    /**
     * When the result stops being true though no data changes: local midnight for a countdown or a
     * streak, a deadline for an overdue status, a period's end for a rolling window. Null when only a
     * change in its data can change it. A result is invalidated at this moment or on a change in a
     * source, whichever comes first, and never on a timer.
     */
    nextInvalidationAt: z.number().int().nonnegative().nullable(),
}).strict();
/** What a result in the unconfigured state is waiting for: the input to set, and who can set it. */
export const widgetNeedSchema = z.object({
    /** The input in people's words: "a target", "the event's date". */
    input: z.string().min(1).max(120),
    /** Who can set it: "a channel admin", "anyone who can edit the channel's records". */
    setBy: z.string().min(1).max(120),
}).strict();
/**
 * A widget's result, given the schema of what its type computes. Only ok and stale carry a value. A
 * denied result carries nothing computed, by construction: it cannot hold a zero or a partial total.
 */
export function widgetResultSchema(value) {
    return z.discriminatedUnion("state", [
        z.object({ state: z.literal("ok"), value, provenance: widgetProvenanceSchema }).strict(),
        z.object({
            state: z.literal("stale"), value, provenance: widgetProvenanceSchema,
            /** The sources older than their freshness promise, by relation. */
            staleSources: z.array(z.string().min(1).max(200)).min(1).max(50),
        }).strict(),
        z.object({ state: z.literal("denied") }).strict(),
        z.object({
            state: z.literal("missing"), provenance: widgetProvenanceSchema,
            /** What there is none of, in people's words: "invoices this month". */
            what: z.string().min(1).max(160).optional(),
        }).strict(),
        z.object({ state: z.literal("unconfigured"), needs: z.array(widgetNeedSchema).min(1).max(8) }).strict(),
        z.object({
            state: z.literal("failed"),
            /** A short reason a person can read; never a stack trace or a query. */
            reason: z.string().min(1).max(200),
            /** Whether asking again may succeed. */
            retryable: z.boolean(),
        }).strict(),
    ]);
}
/**
 * A dashboard view's widgets evaluated for one viewer, by the widget's id in the view's definition:
 * what the tenant answers a view's widget results with. A value is as the widget's type computes it.
 */
export const dashboardWidgetResultsSchema = z.object({
    channelId: z.string().min(1),
    viewId: z.string().min(1),
    objectTypeId: z.string().min(1),
    evaluatedAt: z.number().int().nonnegative(),
    timezone: z.string().min(1).max(64),
    results: z.record(z.string().min(1), widgetResultSchema(z.unknown())),
}).strict();
/** Whether a result has something to draw: its value, perhaps marked as out of date. */
export function widgetResultHasValue(result) {
    return result.state === "ok" || result.state === "stale";
}
/**
 * The sources of a result that are older than their freshness promise at `now`: a source promises to
 * be no older than `promiseMs`, and one whose freshness is not known breaks no promise.
 */
export function staleWidgetSources(sources, promiseMs, now) {
    return sources.filter((source) => source.dataAsOf !== null && now - source.dataAsOf > promiseMs).map((source) => source.relation);
}
/** A computed value as an ok result, or a stale one when any source has outlived its promise. */
export function computedWidgetResult(value, provenance, promiseMs) {
    const staleSources = staleWidgetSources(provenance.sources, promiseMs, provenance.evaluatedAt);
    return staleSources.length > 0 ? { state: "stale", value, provenance, staleSources } : { state: "ok", value, provenance };
}
/** The earliest of the moments a result stops being true; null when none of them is set. */
export function earliestInvalidation(moments) {
    const set = moments.filter((moment) => typeof moment === "number");
    return set.length > 0 ? Math.min(...set) : null;
}
/**
 * The state of something summed up from several results (a headline over a row of widgets, a live
 * summary): the worst of them wins, so a summary is never drawn as complete over a part the viewer
 * may not see or that failed. A summary of nothing is missing.
 */
export function combinedWidgetState(states) {
    const order = ["denied", "failed", "unconfigured", "missing", "stale", "ok"];
    return order.find((state) => states.includes(state)) ?? "missing";
}
